import useStore from "../store.ts";
import {Component, CountdownHide, EffectDirection, Effects, EffectType, HideFlags, RenderEffect} from "../types.ts";
import {Dynamic} from "../channel/DynamicDataParser.ts";
import {postUpdateGeometry} from "../worker/RenderWorkerManager.ts";
import {isProxy, toRaw} from "vue";
import {parseUserAgent} from "./FormatUtils.ts";
import {WrapperSubscription} from "../channel/WrapperSubscription.ts";
import {RenderInvalidationReason, renderInvalidationRegistry} from "./RenderInvalidation.ts";
import type {UpdateContextHandle} from "./UpdateContextRegistry.ts";
import {getParent, parsePx} from "./PureElementUtils.ts";

// see docs/EXTRACTED_COMMENTS.md#c179
export {getParent, parsePx};

export type HideFlagKey = keyof HideFlags;

/* see docs/EXTRACTED_COMMENTS.md#c180 */
export const setHideFlag = (
    id: string,
    flag: HideFlagKey,
    value: boolean,
    _reason: string = "setHideFlag",
    attachToActiveContext: boolean = false,
) => {
    const store = useStore();
    const item = store.components[id];
    if (!item) return;

    if (item.hide_flags[flag] === value) return;

    item.hide_flags[flag] = value;

    // see docs/EXTRACTED_COMMENTS.md#c181
    renderInvalidationRegistry.requestRender(
        RenderInvalidationReason.VisibilityChange,
        [id],
        { shallow: false, attachToActiveContext }
    );
};

export type ContainerRuleResult = { rule?: string; template?: string; result?: boolean };

/**
 * Pick a DataObjectContainer's template and stage the hide flags for it, then mark the container
 * resolved. Store-only: no Vue, no DOM, no mounting.
 *
 * This used to live solely in DataObjectContainer.vue's onMounted, which made `template_resolved`
 * a function of Vue mount order — and ComponentWrapper mounts behind a `v-if` on the published
 * hide flags. A container under a hidden ancestor therefore never mounted, never resolved, and the
 * `template_resolved === false` guard (now applied in Renderer.applyChildrenVisibility, off the
 * `template_resolved` snapshot field) then forced HIDE_WHEN_NO_CHILDREN to answer
 * "stay visible" forever, so the store kept claiming a subtree-empty container was visible
 * (verifier finding on layout 131021, object 131021.03.glane_layout_A.06). Visibility is decided
 * off-thread from store state, so it must not depend on what happens to be mounted.
 *
 * Returns the selected template name (or "" when nothing could be selected).
 */
export const resolveContainerTemplate = (
    item: Component,
    ruleResults: ContainerRuleResult[] = [],
): string => {
    const store = useStore();
    if (!item?.props?.id) return "";
    if (!item.flags) item.flags = {} as any;
    item.flags.template_resolved = true;
    // The RenderWorker guards HIDE_WHEN_NO_CHILDREN on the snapshot's template_resolved field —
    // ship it a fresh snapshot now that the container is resolved.
    renderInvalidationRegistry.requestRender(
        RenderInvalidationReason.VisibilityChange,
        [item.props.id],
        { shallow: true }
    );

    // see docs/EXTRACTED_COMMENTS.md#c036
    const activeRule = ruleResults.find((r) => r.result === true);
    const selectedTemplate = (activeRule ? activeRule.template : item.container_props?.default) ?? "";

    // see docs/EXTRACTED_COMMENTS.md#c037
    const templateNames = new Set<string>(ruleResults.map((r) => r.template).filter((t): t is string => !!t));
    if (item.container_props?.default) templateNames.add(item.container_props.default);

    const suffixOf = (id?: string) => id?.split(".").pop();
    const selectedChild = item.children?.find((c) => suffixOf(c?.props?.id) === selectedTemplate);

    // see docs/EXTRACTED_COMMENTS.md#c038
    for (const child of item.children ?? []) {
        if (child === selectedChild) continue;
        const suffix = suffixOf(child?.props?.id);
        if (!suffix || !templateNames.has(suffix)) continue;
        setHideFlag(child.props.id, "rule", true, "container_rule_hide_other");
    }

    if (!selectedChild || !store.components[selectedChild.props.id]) {
        updateHidden(item.props.id);
        return "";
    }

    setHideFlag(selectedChild.props.id, "rule", false, "container_rule_show");
    setHideFlag(selectedChild.props.id, "render", false, "container_rule_show");
    updateHidden(item.props.id);
    return selectedTemplate;
};

export const updateParent = (id: string) => {
    const store = useStore();
    const parent = getParent(id);

    if (parent != null && store.components[parent] != null) {
        postUpdateGeometry(parent, "parent-update");
    }
}

/* see docs/EXTRACTED_COMMENTS.md#c760 */
export const resolveCountdownEnd = (item: Component): number | null => {
    const staticEnd = Number(item.countdown_props?.data ?? 0);
    if (Number.isFinite(staticEnd) && staticEnd != 0) return staticEnd;

    const field = item.dynamic?.field?.field;
    const raw = field != null ? item.data?.get?.(field)?.value : undefined;
    if (raw == null || raw === "") return null;
    const dynamicEnd = Number(raw);
    return Number.isFinite(dynamicEnd) && dynamicEnd != 0 ? dynamicEnd : null;
};

/** Seconds left on a countdown at `nowSeconds`, or null when it has no end time at all. */
export const countdownDelta = (item: Component, nowSeconds: number): number | null => {
    const end = resolveCountdownEnd(item);
    if (end == null) return null;
    const offset = Number(item.countdown_props?.seconds ?? 0);
    return end - nowSeconds + (offset > 0 ? offset : 0);
};

/* see docs/EXTRACTED_COMMENTS.md#c777 */
export const timerReached = (item: Component, nowSeconds: number): boolean => {
    const field = item.dynamic?.field?.field;
    const raw = field != null ? item.data?.get?.(field)?.value : undefined;
    if (raw == null || raw === "") return false;
    const base = Number(raw);
    if (!Number.isFinite(base)) return false;
    return base + Number(item.timer_props?.seconds ?? 0) <= nowSeconds;
};

/* see docs/EXTRACTED_COMMENTS.md#c777 */
export const updateTimerHidden = (id: string, nowSeconds?: number) => {
    const store = useStore();
    const item = store.components[id];
    if (!item || item.type !== "DataObjectTimer") return;

    const reached = timerReached(item, nowSeconds ?? Math.floor(store.currentDate / 1000));
    const hide = reached !== !!item.timer_props?.show_when_true;

    for (const child of item.children) {
        setHideFlag(child.props.id, "timer", hide, `timer_${reached ? "reached" : "pending"}`);
    }
};

/* see docs/EXTRACTED_COMMENTS.md#c759 */
export const updateCountdownHidden = (id: string, nowSeconds?: number) => {
    const store = useStore();
    const item = store.components[id];
    if (!item || item.type !== "DataObjectCountdown") return;

    const mode = item.countdown_props?.countdown_hide ?? CountdownHide.NONE;
    if (mode !== CountdownHide.COUNTDOWN_END_TIME && mode !== CountdownHide.COUNTDOWN_REACHED) {
        setHideFlag(id, "countdown", false, "countdown_hide_none");
        return;
    }

    const delta = countdownDelta(item, nowSeconds ?? Math.floor(store.currentDate / 1000));
    const hide = mode === CountdownHide.COUNTDOWN_END_TIME
        ? delta === null
        : (delta === null || delta <= 0);

    setHideFlag(id, "countdown", hide, `countdown_hide_${mode}`);
};

export const updateHidden = (id: string) => {
    const store = useStore();
    const item = store.components[id];
    if (!item) return;

    // Orthogonal to auto_hide, and evaluated before the early returns below so a countdown with
    // hide_when_no_match still gets its verdict (see docs/EXTRACTED_COMMENTS.md#c759).
    updateCountdownHidden(id);
    // Same rationale; this is also the data-driven half of the timer verdict, the tick being the
    // other half (see docs/EXTRACTED_COMMENTS.md#c777).
    updateTimerHidden(id);

    if (item.dynamic && (!!item.dynamic.hide_when_no_match && false === item.dynamic.found)) {
        setHideFlag(id, "data", true, "hide_when_no_match");
        setHideFlag(id, "image", false, "hide_when_no_match");
        renderInvalidationRegistry.requestRender(
            RenderInvalidationReason.VisibilityChange,
            [id],
            { shallow: true }
        );
        return;
    }
    let current_state = item.hide_flags.data;
    let new_state: boolean = getNewState(item.auto_hide, item);

    if (current_state != new_state) {
        setHideFlag(id, "data", new_state, "auto_hide");
        setHideFlag(id, "image", false, "auto_hide");
        renderInvalidationRegistry.requestRender(
            RenderInvalidationReason.VisibilityChange,
            [id],
            { shallow: false }
        );
    } else if (item.auto_hide === "HIDE_WHEN_NO_DATA_AND_NO_CHILDREN") {
        // AND mode never stages `data` (getNewState returns false for it), so a data change
        // flips no flag here — but the renderer composes the mode from the `no_data` snapshot
        // input, so ship it a fresh snapshot. postUpdateGeometry dedupes repeats.
        renderInvalidationRegistry.requestRender(
            RenderInvalidationReason.VisibilityChange,
            [id],
            { shallow: true }
        );
    }

    // No ancestor propagation: the HIDE_WHEN_*CHILDREN modes are renderer-owned
    // (hide_flags.children, computed per tick in Renderer.applyChildrenVisibility). The child
    // flag change staged above reaches the RenderWorker through the geometry re-ingest the
    // requestRender above enqueues, and the next tick re-derives every ancestor's flag.
}

// see docs/EXTRACTED_COMMENTS.md#c183
export const applyDataAndUpdateHidden = (id: string, data: any) => {
    const store = useStore();
    const item = store.components[id];
    if (!item) return;
    item.data = data;
    updateHidden(id);
};

/**
 * The HIDE_WHEN_NO_DATA condition, as a bare verdict. Used by getNewState to stage
 * `hide_flags.data`, and shipped on every geometry snapshot as the `no_data` input so the
 * RenderWorker can compose HIDE_WHEN_NO_DATA_AND_NO_CHILDREN (see Renderer.applyChildrenVisibility).
 */
export function computeNoData(item: Component): boolean {
    const data = item.data;
    if (data != null && data.size > 0) {
        const fieldId = item.dynamic?.field?.field;
        if (fieldId) {
            if (!data.has(fieldId)) return true;
            const value = data.get(fieldId)?.value;
            return value == null || value?.length == 0;
        }
        return false;
    }
    return true;
}

export function getNewState(auto_hide: string, item: Component): boolean {
    switch (auto_hide) {
        case "NO_HIDE": {
            return false;
        }
        case "HIDE_WHEN_NO_DATA": {
            return computeNoData(item);
        }
        // The *CHILDREN modes are renderer-owned: the RenderWorker derives hide_flags.children
        // every tick (Renderer.applyChildrenVisibility) and publishes it via HIDE_FLAGS_COMMIT.
        // The main thread never stages `data` for them...
        case "HIDE_WHEN_NO_CHILDREN":
        case "HIDE_WHEN_NO_DATA_AND_NO_CHILDREN": {
            return false;
        }
        // ...except OR mode, whose no-data half hides on its own: it stays in `data`, and the
        // ComponentWrapper v-if ORs it with the renderer's `children` flag.
        case "HIDE_WHEN_NO_DATA_OR_NO_CHILDREN": {
            return computeNoData(item);
        }
    }
    return false;
}





export async function updateChildren(id: string, newRow: number | null, channels: number[], handle?: UpdateContextHandle) {
    const store = useStore();

    // see docs/EXTRACTED_COMMENTS.md#c189
    const stack: Array<{ id: string; expanded: boolean }> = [{ id, expanded: false }];

    while (stack.length > 0) {
        const frame = stack.pop()!;
        const item = store.components[frame.id];
        if (!item) continue;

        // A nested lookup on a cascaded channel resolves its own row: it and its subtree are not this cascade's.
        const dyn = item.dynamic;
        if (dyn?.is_dynamic && [dyn.dynamic_key_channel, dyn.dynamic_channel].some(c => c != null && channels.includes(c))) continue;

        if (!frame.expanded) {
            stack.push({ id: frame.id, expanded: true });
            const children = item.children ?? [];
            for (let i = children.length - 1; i >= 0; i--) {
                stack.push({ id: children[i].props.id, expanded: false });
            }
            continue;
        }

        if (!item.dynamic) continue;
        if (item.dynamic.original_object.row != null) continue;

        const keyMatches = item.dynamic.key_channel != null && channels.includes(item.dynamic.key_channel);
        const channelMatches = item.dynamic.channel != null && channels.includes(item.dynamic.channel);

        if (newRow != null) {
            if (!keyMatches && !channelMatches) continue;

            if (!keyMatches) {
                const key = item.dynamic.key_channel;
                item.dynamic.key_channel = item.dynamic.channel;
                item.dynamic.channel = key;
            }
            item.dynamic.row = newRow as number;
            item.dynamic.found = true;
        } else {
            if (channels.length > 0 && !keyMatches && !channelMatches) continue;
            item.dynamic.row = null;
            item.dynamic.found = false;

            // see docs/EXTRACTED_COMMENTS.md#c190
            if (item.subscription && !item.dynamic.is_dynamic && !item.dynamic.adopt_object) {
                await (item.subscription as WrapperSubscription).clearForCascade(handle);
                continue;
            }
        }

        if (item.subscription) {
            await item.subscription.updateData("", -1, [], handle);
        }
    }
}





export const inheritFont = (id: string, current: string): string => {
    const store = useStore();
    let parentId = getParent(id);
    if (parentId == null) return current;
    let parent = store.components[parentId];
    if (!parent) return current;
    if (parent.style.fontFamily !== undefined) {
        let font_family = parent.style.fontFamily;
        if (font_family != current) {
            return font_family;
        }
    }
    return inheritFont(parentId, current);
}

export const getSize = (item: Component, sizing: string): number | string | null => {
    const parent = getParent(item.props.id);
    const store = useStore();
    const parentElement = parent != null ? store.components[parent] : null;

    switch (sizing) {
        // see docs/EXTRACTED_COMMENTS.md#c191
        case 'AUTOSIZE_PARENT_WIDTH': {
            let parentWidth = 0;
            if (parent == null) {
                parentWidth = window.innerWidth;
            } else if (parentElement != null) {
                parentWidth = parsePx(parentElement.props.style.width);
            }
            return parentWidth - parsePx(item.style.left) + parsePx(item.style.width);
        }
        case 'AUTOSIZE_PARENT_HEIGHT': {
            let parentHeight = 0;
            if (parent == null) {
                parentHeight = window.innerHeight;
            } else if (parentElement != null) {
                parentHeight = parsePx(parentElement.props.style.height);
            }
            return parentHeight - parsePx(item.style.top) + parsePx(item.style.height);
        }
        default: {
            // console.log(`MISSING CASE FOR ${sizing}`)
        }
    }
    return null;
}

const SOURCE_TOKENS = /\{(RL_BROWSER|RL_BROWSER_VERSION|DS_CONNECTION_STATE|DP_OS_VERSION|RL_HOSTNAME|SCREEN_RESOLUTION|page)}/g;

export function formatSource(text: string, _id: string): string {
    if (!text || !text.includes("{")) return text;

    let agent: ReturnType<typeof parseUserAgent> | null = null;
    const ua = () => (agent ??= parseUserAgent());

    return text.replace(SOURCE_TOKENS, (match, token: string) => {
        switch (token) {
            case "RL_BROWSER":
                return ua().browser;
            case "RL_BROWSER_VERSION":
                return ua().version;
            case "DS_CONNECTION_STATE":
                return useStore().connected ? "Connected" : "Disconnected";
            case "DP_OS_VERSION":
                return ua().os;
            case "RL_HOSTNAME":
                return ua().hostname;
            case "SCREEN_RESOLUTION":
                return `${window.innerWidth}x${window.innerHeight}`;
            case "page":
                return `${useStore().layout}`;
            default:
                return match;
        }
    });
}

export function deproxy(v: any): any {
    if (isProxy(v)) v = toRaw(v);
    if (v instanceof Set) {
        v = (Array.from(v.values()));
    }
    if (Array.isArray(v)) return v.map(deproxy);
    if (v && typeof v === 'object') {
        return Object.fromEntries(Object.entries(v).map(([k, val]) => [k, deproxy(val)]));
    }
    return v;
}

/* see docs/EXTRACTED_COMMENTS.md#c573 */
export function effectTimeToMs(t: number | undefined): number {
    const v = Number(t ?? 0);
    if (!Number.isFinite(v) || v <= 0) return 0;
    return v < 10 ? v * 1000 : v;
}


export const cloneComponent = (element: Component) => {
    const store = useStore();
    const component: Component = {
        type: `${element.type}`,
        object_name: element.object_name,
        dynamic: new Dynamic(element.dynamic.original_object, store.clone.transform),
        sizing: `${element.sizing}`,
        anchor: Object.assign({}, element.anchor),
        props: {
            id: `${element.props.id}`,
            style: Object.assign({}, element.style),
            key: `${element.props.key}`,
            // see docs/EXTRACTED_COMMENTS.md#c574
            ...(element.props.class ? { class: element.props.class } : {}),
        },
        published: {
            src: `${element.published.src}`,
            hide_flags: {...element.hide_flags},
        },
        flags: {
            updated_data: false,
            // Renderer-owned; keep main-thread default false.
            updated_position: false,
            geometry_settled: false,
            calculated_row: element.type !== "DataObjectDatasheet", // Preserve datasheet calculated_row state
            is_datasheet: element.flags.is_datasheet,
            is_cluster_sheet: element.flags.is_cluster_sheet,
        },
        auto_size: `${element.auto_size}`,
        children: [],
        hide_flags: {
            // see docs/EXTRACTED_COMMENTS.md#c575
            data: (element.auto_hide !== "NO_HIDE") && !element.auto_hide.includes("CHILDREN"),
            render: false,
            rule: false,
            alternating: false,
            image: false,
            anchoring: false,
            dedup: false,
            playlist: false,
            countdown: false,
            timer: false,
            children: false,
        },
        data: {},
        alternating_props: element.alternating_props,
        button_props: Object.assign({}, element.button_props), // Otherwise mem reference is created.
        submit_button_props: element.submit_button_props,
        ticker_props: element.ticker_props,
        text_props: element.text_props,
        image_props: element.image_props,
        decision_props: Object.assign({}, element.decision_props),
        container_props: element.container_props,
        playlist_props: element.playlist_props,
        digital_clock_props: element.digital_clock_props,
        analog_clock_props: element.analog_clock_props,
        progress_bar_props: element.progress_bar_props,
        function_props: element.function_props,
        countdown_props: element.countdown_props,
        timer_props: element.timer_props,
        input_text_props: element.input_text_props,
        editor_props: element.editor_props,
        html_props: element.html_props,
        datasheet_colors: element.datasheet_colors ? [...element.datasheet_colors] : [],
        is_dynamic: element.is_dynamic,
        style: Object.assign({}, element.style),
        scale: element.scale,
        auto_hide: `${element.auto_hide}`,
        overlay: element.overlay,
        viewType: `${element.viewType}`,
        adoptSubscriptions: {},
        adoptData: new Map(),
        parent: String(getParent(element.props.id) || Object.keys(store.layoutData)[0].replace("_", "")),
        subscription: new WrapperSubscription(""),
        effects: element.effects,
    }
    return component;
}


// see docs/EXTRACTED_COMMENTS.md#c807
function effectOn(sub: any): boolean {
    return !!sub && sub.on === true;
}

function parseEffectType(effect_type: any, is_in: boolean | null = null): EffectType {
    if (effectOn(effect_type?.crossfade)) {
        return EffectType.CROSS_FADE
    }
    if (effectOn(effect_type?.fly)) {
        return EffectType.FLY;
    }
    if (effectOn(effect_type?.curtain)) {
        return EffectType.CURTAIN
    }
    if (effectOn(effect_type?.turn)) {
        return EffectType.TURN
    }
    if (is_in) {
        return EffectType.IN
    }else if(!is_in && is_in != null) {
        return EffectType.OUT
    }

    // Fallback. Might need to change.
    return EffectType.TURN
}

// see docs/EXTRACTED_COMMENTS.md#c576
function parseEffectDirection(effect_type: any): EffectDirection | undefined {
    const active = [effect_type?.crossfade, effect_type?.fly, effect_type?.curtain, effect_type?.turn].find(effectOn);
    const raw = active?.direction;
    if (typeof raw !== "string") return undefined;
    const dir = raw.replace(/^MOVE_/, "").toUpperCase();
    switch (dir) {
        case "UP":
        case "DOWN":
        case "LEFT":
        case "RIGHT":
        case "UP_LEFT":
        case "UP_RIGHT":
        case "DOWN_LEFT":
        case "DOWN_RIGHT":
            return dir;
        default:
            return undefined;
    }
}

export const parseEffects = (effects: any): Effects | undefined => {
    if (!effects) return undefined;
    const focus_in_events = [];
    const focus_out_events = [];

    for (const eff of (effects.focus_change_in || [{}])) {
        if (!eff) continue;
        focus_in_events.push({
            effect_sequence: eff.effect_sequence,
            effect_time: eff.effect_time || 1,
            effect_type: parseEffectType(eff.effect_type, true),
            direction: parseEffectDirection(eff.effect_type),
        } as RenderEffect);
    }

    for (const eff of (effects.focus_change_out || [{}])) {
        if (!eff) continue;
        focus_out_events.push({
            effect_sequence: eff.effect_sequence || 0,
            effect_time: eff.effect_time || 1,
            effect_type: parseEffectType(eff.effect_type, false),
            direction: parseEffectDirection(eff.effect_type),
        } as RenderEffect);
    }

    return {
        focus_change_in: focus_in_events.sort((a, b) => (a.effect_sequence - b.effect_sequence)),
        focus_change_out: focus_out_events.sort((a, b) => (a.effect_sequence - b.effect_sequence)),
    } as Effects;
};

export const transformChannel = (item: any, store: ReturnType<typeof useStore>) => {
    for (let transform of store.clone.transform.channel_map || []) {
        const { source, dest } = transform;
        if (item.dynamic.channel == source) {
            item.dynamic.channel = dest;
        }
        if (item.dynamic.key_channel == source) {
            item.dynamic.key_channel = dest;
        }
        if (item.dynamic.dynamic_channel == source) {
            item.dynamic.dynamic_channel = dest;
        }
        if (item.dynamic.dynamic_key_channel == source) {
            item.dynamic.dynamic_key_channel = dest;
        }
    }
}